import { useEffect, useState } from "react";
import { useApi } from "../lib/api";
import { useCurrentUser } from "../lib/useCurrentUser";
import { Card } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import type { NotificationRow } from "../lib/types";

// /me/notifications is the cheapest signal we have for "does this user have a
// pending invite waiting?" Without an invite-specific endpoint, we inspect
// the notification feed for an invite_created event tied to this user.
interface InviteHint {
  role: string;
  organization_id: number | null;
}

function readInviteHint(notifs: NotificationRow[]): InviteHint | null {
  for (const n of notifs) {
    if (n.event_type !== "invite_created") continue;
    try {
      const p = JSON.parse(n.payload_json || "{}");
      if (typeof p.role === "string") {
        return {
          role: p.role,
          organization_id:
            typeof p.organization_id === "number" ? p.organization_id : null,
        };
      }
    } catch {
      // ignore malformed payload
    }
  }
  return null;
}

export default function AccessPending() {
  const { user, refetch } = useCurrentUser();
  const api = useApi();
  const [hint, setHint] = useState<InviteHint | null>(null);
  const [recheckBusy, setRecheckBusy] = useState(false);

  useEffect(() => {
    api
      .get<{ notifications: NotificationRow[] }>("/api/me/notifications")
      .then((r) => setHint(readInviteHint(r.notifications ?? [])))
      .catch(() => setHint(null));
  }, [api]);

  const recheck = async () => {
    setRecheckBusy(true);
    try {
      // /api/me re-runs applyPendingInvite — so if an invite was issued after
      // the user first signed in, this picks it up without making them sign
      // out and back in.
      await api.get("/api/me");
      refetch();
    } catch {
      // ignore
    } finally {
      setRecheckBusy(false);
    }
  };

  const inviteAccepted =
    user?.role !== undefined &&
    user.role !== "community_member";

  return (
    <div className="container-page py-16">
      <Card className="mx-auto max-w-xl">
        <div className="text-center">
          <Badge tone={inviteAccepted ? "success" : "warning"}>
            {inviteAccepted ? "Access granted" : "Access pending"}
          </Badge>
          <h1 className="mt-4 text-2xl font-semibold text-white">
            {inviteAccepted
              ? "Your invite has been accepted"
              : "Community Shield OS is invite-only"}
          </h1>
        </div>

        {inviteAccepted ? (
          <div className="mt-4 space-y-3 text-sm text-slate-300">
            <p>
              You're in. Head to the dashboard — you should see content tied
              to your role
              {user?.organization_id ? " and organization" : ""}.
            </p>
            <div className="mt-4 flex justify-center">
              <a
                href="/dashboard"
                className="inline-flex items-center rounded-md bg-accent px-4 py-2 text-sm font-semibold text-ink-950 hover:bg-accent/90"
              >
                Go to dashboard
              </a>
            </div>
          </div>
        ) : (
          <div className="mt-4 space-y-3 text-sm text-slate-300">
            {user?.email && (
              <p>
                Your account{" "}
                <span className="text-white">{user.email}</span> is signed in
                but isn't yet linked to a pilot organization.
              </p>
            )}

            {hint && (
              <div className="rounded-md border border-emerald-400/30 bg-emerald-400/5 px-4 py-3 text-emerald-100">
                <div className="text-xs uppercase tracking-wider text-emerald-200">
                  Invite waiting
                </div>
                <p className="mt-1 text-sm">
                  We found a pending invite for your email with role{" "}
                  <span className="font-semibold">
                    {hint.role.replace("_", " ")}
                  </span>
                  {hint.organization_id
                    ? ` and an organization assigned.`
                    : "."}{" "}
                  Tap “Check again” and the invite will activate on this
                  account.
                </p>
              </div>
            )}

            <ol className="ml-5 list-decimal space-y-1 text-slate-300">
              <li>
                Ask a platform admin to send an invite to{" "}
                <span className="text-white">
                  {user?.email ?? "your account email"}
                </span>
                .
              </li>
              <li>
                Once issued, tap <span className="text-white">Check again</span>
                {" "}below — or sign out and back in.
              </li>
              <li>
                If you reach a verified pilot member, they can vouch for you to
                an admin.
              </li>
            </ol>

            <p className="rounded-md border border-white/10 bg-white/[0.03] px-4 py-3 text-xs text-slate-400">
              Need help? Contact your community's platform admin (or reply to
              the email that introduced you to the pilot). Confirm the email
              address you signed up with matches the invite — Clerk and our
              database are case-insensitive but exact characters matter.
            </p>

            <div className="mt-4 flex justify-center gap-2">
              <Button onClick={recheck} disabled={recheckBusy}>
                {recheckBusy ? "Checking…" : "Check again"}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
